import React, { useEffect, useRef, useState } from 'react';
import { CarProfile, Ticket, SimCard, ArrowRight } from '@phosphor-icons/react';
import { TravelpayoutsWidget } from './TravelpayoutsWidget';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  CAR_RENTAL_WIDGET_SRC, ESIM_WIDGET_SRC, TOUR_CITIES, toursWidgetSrc,
} from '../config/travelpayouts';
import { trackEvent } from '../utils/analytics';

// Cross-sells around the transfer: the transfer brings the visitor in, these
// earn the extra commission (GetRentacar 10%, Airalo 12%, Tiqets 3.5–8%).
export const EXTRAS = {
  cars: {
    icon: CarProfile,
    tab: 'Car rental',
    title: 'Rent a car at your destination',
    text: 'Compare rental cars from local and global suppliers.',
    cta: 'Find a car',
  },
  tours: {
    icon: Ticket,
    tab: 'Tours',
    title: 'Tours & attraction tickets',
    text: 'Skip-the-line tickets and top-rated tours.',
    cta: 'See tours',
  },
  esim: {
    icon: SimCard,
    tab: 'eSIM',
    title: 'Mobile data with an eSIM',
    text: 'Stay online abroad without roaming fees.',
    cta: 'Get an eSIM',
  },
};

// Tiqets takes 10–20 s to fill its iframe, which starts at 150px and grows once
// the tours arrive. Until then the box is blank, so show a loading note.
function useTiqetsLoaded(containerRef, cityId) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setLoaded(false);
    const timer = setInterval(() => {
      const frame = containerRef.current?.querySelector('iframe');
      if (frame && frame.offsetHeight > 200) {
        setLoaded(true);
        clearInterval(timer);
      }
    }, 500);
    return () => clearInterval(timer);
  }, [containerRef, cityId]);
  return loaded;
}

function ToursPanel({ initialCity, currency }) {
  const [cityId, setCityId] = useState((initialCity || TOUR_CITIES[0]).tiqetsId);
  const widgetRef = useRef(null);
  const loaded = useTiqetsLoaded(widgetRef, cityId);
  const cityName = TOUR_CITIES.find(c => c.tiqetsId === cityId)?.name;
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="tour-city-picker">
        {TOUR_CITIES.map(c => (
          <button
            key={c.tiqetsId}
            type="button"
            onClick={() => setCityId(c.tiqetsId)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              c.tiqetsId === cityId
                ? 'bg-[#1a3a6e] text-white border-[#1a3a6e]'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>
      <div ref={widgetRef} className="relative">
        {!loaded && (
          <div className="absolute inset-x-0 top-0 h-[150px] flex items-center justify-center gap-2 text-sm text-slate-500 bg-white z-10" data-testid="tours-loading">
            <span className="w-4 h-4 border-2 border-slate-300 border-t-[#1a3a6e] rounded-full animate-spin" />
            Loading tours in {cityName}…
          </div>
        )}
        {/* key forces a fresh widget per city — the loader only renders once per tag */}
        <TravelpayoutsWidget key={cityId} src={toursWidgetSrc(cityId, currency)} dataTestId="tours-widget" />
      </div>
    </div>
  );
}

// The widget for one kind of extra. Each loader script is heavy, so callers
// mount this only when the visitor actually asks for that extra.
export function ExtraPanel({ kind, city, currency = 'EUR' }) {
  if (kind === 'cars') return <TravelpayoutsWidget src={CAR_RENTAL_WIDGET_SRC} dataTestId="cars-widget" />;
  if (kind === 'esim') return <TravelpayoutsWidget src={ESIM_WIDGET_SRC} dataTestId="esim-widget" />;
  if (kind === 'tours') return <ToursPanel initialCity={city} currency={currency} />;
  return null;
}

// Cards that open the matching widget in a dialog. Used beside the results
// and after payment, where the visitor should not be sent away from the page.
export function TravelExtrasCards({ city, currency, placement, columns = 1, kinds = ['esim', 'cars', 'tours'] }) {
  const [open, setOpen] = useState(null);
  const active = open && EXTRAS[open];

  const show = (kind) => {
    trackEvent('cross_sell_opened', { kind, placement });
    setOpen(kind);
  };

  return (
    <>
      <div className={`grid gap-3 ${columns === 3 ? 'sm:grid-cols-3' : ''}`} data-testid={`travel-extras-${placement}`}>
        {kinds.map(kind => {
          const extra = EXTRAS[kind];
          const Icon = extra.icon;
          const title = kind === 'tours' && city ? `Things to do in ${city.name}` : extra.title;
          return (
            <button
              key={kind}
              type="button"
              onClick={() => show(kind)}
              className="group text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-[#1a3a6e]/50 hover:shadow-md transition-all"
              data-testid={`extra-card-${kind}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1a3a6e]/10 flex items-center justify-center flex-shrink-0">
                  <Icon size={20} className="text-[#1a3a6e]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 leading-snug">{title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{extra.text}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0071c2] mt-2 group-hover:gap-1.5 transition-all">
                    {extra.cta} <ArrowRight size={12} />
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle>{open === 'tours' && city ? `Things to do in ${city.name}` : active.title}</DialogTitle>
                <DialogDescription>{active.text}</DialogDescription>
              </DialogHeader>
              <ExtraPanel kind={open} city={city} currency={currency} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
